import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { ChatCircleDots, X, Bug, Lightbulb, ChatText, PaperPlaneRight, CheckCircle, Paperclip } from '@phosphor-icons/react';
import { toast } from 'sonner';
import API_URL from '../config';

const TYPES = [
  { key: 'bug', label: 'Bug', icon: Bug },
  { key: 'idea', label: 'Idea', icon: Lightbulb },
  { key: 'general', label: 'General', icon: ChatText },
];

// Downscale an image file to a small JPEG data URL (max 1280px, quality 0.7).
function downscaleImage(file, maxDim = 1280, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Floating client feedback / bug-report widget. Posts to the backend, which stores
// the message (+ optional screenshot) and alerts the team in Slack with auto-captured context.
export default function FeedbackWidget() {
  const location = useLocation();
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const reset = () => { setType('bug'); setMessage(''); setScreenshot(null); setSent(false); };
  const close = () => { setOpen(false); setTimeout(reset, 250); };

  // Let other components (e.g. the beta banner) open this widget.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-feedback', handler);
    return () => window.removeEventListener('open-feedback', handler);
  }, []);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file.'); return; }
    try {
      const dataUrl = await downscaleImage(file);
      setScreenshot(dataUrl);
    } catch {
      toast.error("Couldn't read that image — try another.");
    }
  };

  const submit = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/client/feedback`, {
        type,
        message: message.trim(),
        page_path: location.pathname,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screenshot: screenshot || undefined,
      });
      setSent(true);
    } catch (e) {
      const msg = e?.response?.status === 429
        ? "You've sent a few messages already — please try again a little later."
        : 'Something went wrong sending that — please try again.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full shadow-lg transition-transform hover:-translate-y-0.5"
          style={{ backgroundColor: '#141b4d', color: '#ffffff' }}
          data-testid="feedback-fab"
        >
          <ChatCircleDots weight="fill" className="w-5 h-5" style={{ color: '#ffd100' }} />
          <span className="text-sm font-semibold">Feedback</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-96 bg-white shadow-2xl"
          style={{ border: '1px solid #ece5d8' }}
          data-testid="feedback-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: '#141b4d' }}>
            <div className="flex items-center gap-2">
              <ChatCircleDots weight="fill" className="w-5 h-5" style={{ color: '#ffd100' }} />
              <span className="text-sm font-bold text-white">Report a bug or share feedback</span>
            </div>
            <button onClick={close} className="text-white/80 hover:text-white" data-testid="feedback-close" aria-label="Close">
              <X weight="bold" className="w-5 h-5" />
            </button>
          </div>

          {sent ? (
            <div className="px-5 py-8 text-center" data-testid="feedback-success">
              <CheckCircle weight="fill" className="w-10 h-10 mx-auto mb-3" style={{ color: '#006747' }} />
              <p className="text-base font-semibold" style={{ color: '#101820' }}>Thanks — we got it.</p>
              <p className="text-sm mt-1" style={{ color: '#9a9488' }}>Our team will take a look. If we need more detail, we'll email you.</p>
              <button onClick={close} className="mt-5 text-sm font-semibold hover:underline" style={{ color: '#1d57a5' }} data-testid="feedback-done-btn">Done</button>
            </div>
          ) : (
            <div className="p-4">
              {/* Type toggle */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {TYPES.map((t) => {
                  const active = type === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setType(t.key)}
                      className="flex items-center justify-center gap-1.5 py-2 text-sm font-semibold transition-colors"
                      style={active
                        ? { backgroundColor: '#141b4d', color: '#ffffff' }
                        : { backgroundColor: '#f2e9db', color: '#52525B' }}
                      data-testid={`feedback-type-${t.key}`}
                    >
                      <t.icon weight={active ? 'fill' : 'regular'} className="w-4 h-4" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                maxLength={4000}
                placeholder={type === 'bug'
                  ? "What went wrong? Where did you see it?"
                  : type === 'idea'
                    ? "What would make this better for you?"
                    : 'Tell us anything…'}
                className="w-full p-3 text-sm resize-none focus:outline-none"
                style={{ border: '1px solid #ece5d8', color: '#101820' }}
                data-testid="feedback-message"
              />

              {/* Screenshot attach */}
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="feedback-file-input" />
              {screenshot ? (
                <div className="mt-2 flex items-center gap-3 p-2" style={{ border: '1px solid #ece5d8' }} data-testid="feedback-screenshot-preview">
                  <img src={screenshot} alt="Screenshot preview" className="w-14 h-14 object-cover" style={{ border: '1px solid #ece5d8' }} />
                  <span className="text-sm flex-1" style={{ color: '#101820' }}>Screenshot attached</span>
                  <button onClick={() => setScreenshot(null)} className="text-sm font-semibold hover:underline" style={{ color: '#9a9488' }} data-testid="feedback-screenshot-remove">Remove</button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                  style={{ color: '#1d57a5' }} data-testid="feedback-attach-btn">
                  <Paperclip weight="bold" className="w-4 h-4" /> Attach a screenshot
                </button>
              )}

              <button
                onClick={submit}
                disabled={!message.trim() || sending}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 text-sm font-bold transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                style={{ backgroundColor: '#1d57a5', color: '#ffffff' }}
                data-testid="feedback-submit"
              >
                {sending ? 'Sending…' : <>Send <PaperPlaneRight weight="fill" className="w-4 h-4" /></>}
              </button>
              <p className="text-xs mt-2 text-center" style={{ color: '#9a9488' }}>
                We'll see the page you're on — no need to explain where.
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
}
